'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  ScheduleSlotsDay,
  groupSlotsByDate,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_utils/group-slots-by-date';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { getSchedulesSlotById } from '@/app/(main)/(pages)/busca/[slug]/_services/get-schedules-slot-by-id.service';

export type { ScheduleSlotsDay };

export interface UseScheduleSlotsParams {
  scheduleId: string | undefined;
  limitDays: number;
}

export interface UseScheduleSlotsReturn {
  isLoading: boolean;
  days: ScheduleSlotsDay[];
  hasMore: boolean;
  offset: number;
  goNext(): void;
  goPrev(): void;
}

export function useScheduleSlots({
  scheduleId,
  limitDays,
}: UseScheduleSlotsParams): UseScheduleSlotsReturn {
  const { filters } = useSearchFilter();
  const { dayTime, minDate, maxDate } = filters;

  const [days, setDays] = useState<ScheduleSlotsDay[]>([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [prevScheduleId, setPrevScheduleId] = useState(scheduleId);
  const [prevDayTime, setPrevDayTime] = useState(dayTime);
  const [prevMinDate, setPrevMinDate] = useState(minDate);
  const [prevMaxDate, setPrevMaxDate] = useState(maxDate);

  if (
    scheduleId !== prevScheduleId
    || dayTime !== prevDayTime
    || minDate !== prevMinDate
    || maxDate !== prevMaxDate
  ) {
    setPrevScheduleId(scheduleId);
    setPrevDayTime(dayTime);
    setPrevMinDate(minDate);
    setPrevMaxDate(maxDate);
    setOffset(0);
    setDays([]);
    setHasMore(true);
  }

  useEffect(() => {
    if (!scheduleId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getSchedulesSlotById({ id: scheduleId, limitDays, offsetDays: offset, dayTime, minDate, maxDate })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setDays(groupSlotsByDate(result.slots));
        setHasMore(result.hasMore);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setDays([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleId, limitDays, offset, dayTime, minDate, maxDate]);

  const goNext = useCallback(() => {
    if (!hasMore) {
      return;
    }
    setOffset((current) => current + limitDays);
  }, [hasMore, limitDays]);

  const goPrev = useCallback(() => {
    setOffset((current) => Math.max(0, current - limitDays));
  }, [limitDays]);

  return { isLoading, days, hasMore, offset, goNext, goPrev };
}
